import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { NodeStatus } from '../../../types/artifactory';
import { FiFile, FiFolder, FiRefreshCw, FiAlertTriangle, FiCheckCircle, FiClock, FiLock, FiTarget, FiCalendar } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';

interface HydrationRowCardProps {
  node: {
    id: string;
    path: string;
    status: NodeStatus;
    type?: string;
    dependencies?: Array<{
      id: string;
      path: string;
      status: NodeStatus;
    }>;
    metadata?: Record<string, any>;
    assigned_persona_id?: string;
    created_at?: string | Date;
    updated_at?: string | Date;
  };
  isCompleted?: boolean;
  isCurrentlyHydrating?: boolean;
  completedNodes?: Set<string>;
  onFocusInGraph?: (nodeId: string) => void;
  position?: { x: number, y: number };
  visible?: boolean;
  showFocusButton?: boolean;
}

export const HydrationRowCard: React.FC<HydrationRowCardProps> = ({
  node,
  isCompleted = false,
  isCurrentlyHydrating = false,
  completedNodes = new Set(),
  onFocusInGraph,
  position,
  visible = false,
  showFocusButton = false
}) => {
  const { darkMode } = useTheme();
  
  if (!visible) return null;

  // Get status icon
  const getStatusIcon = (status: NodeStatus) => {
    switch (status) {
      case NodeStatus.GHOST: return <FiFile className="text-gray-400" />;
      case NodeStatus.PENDING: return <FiClock className="text-yellow-500" />;
      case NodeStatus.HYDRATING: return <FiRefreshCw className="text-blue-500 animate-spin" />;
      case NodeStatus.FRESH: return <FiCheckCircle className="text-green-500" />;
      case NodeStatus.STALE: return <FiAlertTriangle className="text-orange-500" />;
      case NodeStatus.LOCKED: return <FiLock className="text-red-500" />;
      default: return <FiFile className="text-gray-400" />;
    }
  };

  // Get status color class
  const getStatusColorClass = (status: NodeStatus) => {
    switch (status) {
      case NodeStatus.GHOST: return darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-300';
      case NodeStatus.PENDING: return darkMode ? 'bg-yellow-900 border-yellow-700' : 'bg-yellow-50 border-yellow-300';
      case NodeStatus.HYDRATING: return darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-300';
      case NodeStatus.FRESH: return darkMode ? 'bg-green-900 border-green-700' : 'bg-green-50 border-green-300';
      case NodeStatus.STALE: return darkMode ? 'bg-orange-900 border-orange-700' : 'bg-orange-50 border-orange-300';
      case NodeStatus.LOCKED: return darkMode ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-300';
      default: return darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-300';
    }
  };

  // Get status text color
  const getStatusTextColor = (status: NodeStatus) => {
    switch (status) {
      case NodeStatus.GHOST: return darkMode ? 'text-gray-300' : 'text-gray-700';
      case NodeStatus.PENDING: return darkMode ? 'text-yellow-300' : 'text-yellow-700';
      case NodeStatus.HYDRATING: return darkMode ? 'text-blue-300' : 'text-blue-700';
      case NodeStatus.FRESH: return darkMode ? 'text-green-300' : 'text-green-700';
      case NodeStatus.STALE: return darkMode ? 'text-orange-300' : 'text-orange-700';
      case NodeStatus.LOCKED: return darkMode ? 'text-red-300' : 'text-red-700';
      default: return darkMode ? 'text-gray-300' : 'text-gray-700';
    }
  };

  // Get file type from path
  const getFileType = (path: string) => {
    const extension = path.split('.').pop()?.toLowerCase();
    if (!extension) return 'Unknown';
    
    switch (extension) {
      case 'js': return 'JavaScript';
      case 'jsx': return 'React JSX';
      case 'ts': return 'TypeScript';
      case 'tsx': return 'React TSX';
      case 'py': return 'Python';
      case 'html': return 'HTML';
      case 'css': return 'CSS';
      case 'json': return 'JSON';
      case 'md': return 'Markdown';
      default: return extension.toUpperCase();
    }
  };

  // Get file name from path
  const getFileName = (path: string) => {
    return path.split('/').pop() || path;
  };

  // Calculate position styles
  const positionStyles = position ? {
    position: 'absolute' as const,
    left: `${position.x}px`,
    top: `${position.y}px`,
    transform: 'translate(-100%, -50%)', // Position to the left and align middle with the node
    zIndex: 1000,
  } : {};

  return (
    <div 
      className={`
        ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} 
        border rounded-lg shadow-lg overflow-hidden
        transition-all duration-200 ease-in-out
        ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}
      `}
      style={{
        width: '320px',
        backdropFilter: 'blur(8px)',
        ...positionStyles
      }}
    >
      {/* Header with status */}
      <div className={`p-3 ${getStatusColorClass(node.status)} border-b flex justify-between items-center`}>
        <div className="flex items-center">
          {node.type === 'directory' ? 
            <FiFolder className="text-yellow-500 mr-2" /> : 
            getStatusIcon(node.status)
          }
          <span className={`font-medium ${getStatusTextColor(node.status)}`}>
            {getFileName(node.path)}
          </span>
        </div>
        <div className={`px-2 py-0.5 rounded-full text-xs font-bold ${getStatusTextColor(node.status)}`}>
          {isCurrentlyHydrating ? 'HYDRATING' : node.status.toUpperCase()}
        </div>
      </div>

      {/* Body with details */}
      <div className="p-3 space-y-2">
        {/* Path */}
        <div className="text-xs">
          <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} font-medium`}>Path: </span>
          <span className="font-mono">{node.path}</span>
        </div>

        {/* File Type */}
        <div className="text-xs">
          <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} font-medium`}>Type: </span>
          <span>{node.type === 'directory' ? 'Directory' : getFileType(node.path)}</span>
        </div>

        {/* Persona */}
        {node.assigned_persona_id && (
          <div className="text-xs">
            <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} font-medium`}>Persona: </span>
            <span className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
              {node.assigned_persona_id.replace(/_/g, ' ')}
            </span>
          </div>
        )}
        
        {/* Timestamps */}
        {(node.created_at || node.updated_at) && (
          <div className="text-xs flex flex-col gap-1 mt-1">
            {node.created_at && (
              <div className="flex items-center">
                <FiCalendar className={`mr-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} size={12} />
                <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} font-medium`}>Created: </span>
                <span className="ml-1">
                  {formatDistanceToNow(new Date(node.created_at), { addSuffix: true })}
                  <span className={`ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'} text-[10px]`}>
                    ({new Date(node.created_at).toLocaleString(undefined, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })})
                  </span>
                </span>
              </div>
            )}
            {node.updated_at && node.updated_at !== node.created_at && (
              <div className="flex items-center">
                <FiRefreshCw className={`mr-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} size={12} />
                <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} font-medium`}>Updated: </span>
                <span className="ml-1">
                  {formatDistanceToNow(new Date(node.updated_at), { addSuffix: true })}
                  <span className={`ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'} text-[10px]`}>
                    ({new Date(node.updated_at).toLocaleString(undefined, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })})
                  </span>
                </span>
              </div>
            )}
          </div>
        )}

        {/* Dependencies */}
        {node.dependencies && node.dependencies.length > 0 && (
          <div className="text-xs">
            <div className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} font-medium mb-1`}>
              Dependencies ({node.dependencies.length}):
            </div>
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto custom-scrollbar">
              {node.dependencies.map((dep) => (
                <span
                  key={dep.id}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    completedNodes.has(dep.id) || dep.status === 'fresh'
                      ? darkMode ? 'bg-green-900 bg-opacity-30 text-green-400' : 'bg-green-100 text-green-800'
                      : dep.status === 'stale'
                        ? darkMode ? 'bg-orange-900 bg-opacity-30 text-orange-400' : 'bg-orange-100 text-orange-800'
                        : dep.status === 'ghost'
                          ? darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                          : darkMode ? 'bg-blue-900 bg-opacity-30 text-blue-400' : 'bg-blue-100 text-blue-800'
                  }`}
                  title={`${dep.path} (${dep.status})`}
                >
                  {dep.path.split('/').pop() || dep.path}
                  {(!dep.status || dep.status !== 'fresh') && !completedNodes.has(dep.id) && (
                    <span className="ml-1 w-2 h-2 rounded-full bg-red-500" title="Needs hydration"></span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        {node.metadata && Object.keys(node.metadata).length > 0 && (
          <div className="text-xs">
            <div className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} font-medium mb-1`}>
              Metadata:
            </div>
            <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} max-h-20 overflow-y-auto custom-scrollbar`}>
              {Object.entries(node.metadata).map(([key, value]) => (
                <div key={key} className="mb-1 last:mb-0">
                  <span className="font-medium">{key}: </span>
                  <span className="font-mono">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer with actions */}
      {onFocusInGraph && showFocusButton && (
        <div className={`p-2 border-t ${darkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'} flex justify-end`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onFocusInGraph(node.id);
            }}
            className={`px-2 py-1 rounded text-xs flex items-center ${
              darkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
            }`}
          >
            <FiTarget className="mr-1" />
            Focus in Graph
          </button>
        </div>
      )}

      {/* Arrow pointing to the node */}
      <div 
        className={`absolute w-4 h-4 transform rotate-45 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
        style={{
          right: '-8px',
          top: '50%',
          marginTop: '-8px',
          boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.1)'
        }}
      ></div>
    </div>
  );
};
